import type {
  TypeCurveHeader,
  DevelopmentAreasQueryRequest,
} from 'types/api-responses';
import type { TcMapFilterMode, MapBounds, TcUnitFilters } from './types';
import { snakeCase } from 'lodash-es';

interface BuildParams {
  typeCurveHeader: TypeCurveHeader;
  mapFilterMode: TcMapFilterMode;
  mapBounds: MapBounds | null;
  aoiPolygon: GeoJSON.Polygon | null;
  filters?: TcUnitFilters;
  typeCurveHeaderDetailsId?: number;
  inventoryWorkspaceId?: number;
  formationName?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  text?: string;
}

// Picklist filter params that pass through to the request body as
// `string[]` (or `number[]` for basinTierIds).
const STRING_ARRAY_PARAMS = [
  'names',
  'unitNames',
  'unitTypes',
  'unitDevelopmentTypes',
  'states',
  'unitSources',
  'estimateSources',
] as const;

// Boolean filters stored as picklist arrays of 'true' / 'false' in the atom
// (matches the wells modal pattern). Collapsed to a single boolean for the
// payload — if both options selected, the filter is "any" and we omit it.
const BOOLEAN_PARAMS = ['overlapping', 'removed', 'filedWellOnly'] as const;

function collapseBooleanFilter(
  values: string[] | undefined
): boolean | undefined {
  if (!values || values.length === 0 || values.length === 2) return undefined;
  return values[0] === 'true';
}

/**
 * Converts unit filters into URL search params for the dev-areas tile endpoint.
 * Mirrors the DCA workspace `buildMapTileFilterParams` pattern.
 * @param {TcUnitFilters} filters - Unit picklist / range / boolean filters.
 * @param {object} [options] - Optional text search and workspace id.
 * @return {string} Encoded query string.
 */
export function buildDevAreasTileFilterParams(
  filters: TcUnitFilters,
  options?: {
    typeCurveHeaderDetailsId?: number;
    text?: string;
  }
): string {
  const params = new URLSearchParams();

  if (options?.text?.trim()) {
    params.set('text', options.text.trim());
  }

  if (options?.typeCurveHeaderDetailsId) {
    params.set(
      'type_curve_header_details_id',
      String(options.typeCurveHeaderDetailsId)
    );
  }

  // String array picklist filters (Rails-style key[]=value)
  for (const key of STRING_ARRAY_PARAMS) {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) {
      const snakeKey = snakeCase(key);
      for (const v of value as string[]) {
        params.append(`${snakeKey}[]`, v);
      }
    }
  }

  // Basin tier IDs
  const basinTierIds = filters.basinTierIds;
  if (Array.isArray(basinTierIds) && basinTierIds.length > 0) {
    for (const v of basinTierIds as string[]) {
      params.append('basin_tier_ids[]', v);
    }
  }

  // Numeric range filters (Rails-style key[min]/key[max])
  const numericRanges = ['acreage', 'length', 'width'] as const;
  for (const key of numericRanges) {
    const min = filters[`min_${key}`];
    const max = filters[`max_${key}`];
    if (min !== undefined) params.set(`${key}[min]`, String(min));
    if (max !== undefined) params.set(`${key}[max]`, String(max));
  }

  // Boolean tri-state filters
  for (const key of BOOLEAN_PARAMS) {
    const collapsed = collapseBooleanFilter(filters[key] as string[]);
    if (collapsed !== undefined) {
      params.set(snakeCase(key), String(collapsed));
    }
  }

  // verification_status is a scalar backend param (not array); extract first element.
  const verificationStatus = filters.verificationStatus;
  if (Array.isArray(verificationStatus) && verificationStatus.length > 0) {
    params.set('verification_status', verificationStatus[0] as string);
  }

  return params.toString();
}

export function buildDevelopmentAreasPayload({
  typeCurveHeader,
  mapFilterMode,
  mapBounds,
  aoiPolygon,
  filters,
  typeCurveHeaderDetailsId,
  inventoryWorkspaceId,
  formationName,
  page,
  pageSize,
  sortBy,
  sortOrder,
  text,
}: BuildParams): DevelopmentAreasQueryRequest {
  const payload: DevelopmentAreasQueryRequest = {
    page: page ?? 1,
    pageSize: pageSize ?? 100,
    ...(sortBy && { sort_by: sortBy }),
    ...(sortOrder && { sort_order: sortOrder }),
    ...(text && { text }),
    ...(typeCurveHeaderDetailsId !== undefined && {
      type_curve_header_details_id: typeCurveHeaderDetailsId,
    }),
    ...(inventoryWorkspaceId !== undefined && {
      inventory_workspace_id: inventoryWorkspaceId,
    }),
    ...(formationName !== undefined && {
      formation_name: formationName,
    }),
    ...(typeCurveHeaderDetailsId === undefined && {
      typeCurveHeaderId: typeCurveHeader.id,
    }),
    response_config: {
      include_overlapping_flag: true,
      include_cross_section_line: true,
      include_facets: true,
    },
  };

  switch (mapFilterMode) {
    case 'type-curve-area':
      if (typeCurveHeader.geometry) {
        payload.polygon_ewkb = typeCurveHeader.geometry;
      }
      break;
    case 'visible-extent':
      if (mapBounds) {
        payload.bounding_box = {
          sw_lng: mapBounds.sw_lng,
          sw_lat: mapBounds.sw_lat,
          ne_lng: mapBounds.ne_lng,
          ne_lat: mapBounds.ne_lat,
        };
      }
      break;
    case 'aoi-polygon':
      if (aoiPolygon) {
        payload.polygon_geojson = {
          coordinates: aoiPolygon.coordinates as number[][][],
          type: aoiPolygon.type,
        };
      }
      break;
  }

  if (filters) {
    // String picklists → array passthrough
    for (const key of STRING_ARRAY_PARAMS) {
      const value = filters[key];
      if (Array.isArray(value) && value.length > 0) {
        (payload as Record<string, unknown>)[key] = value;
      }
    }

    // basinTierIds — atom stores as string[]; payload wants number[]
    const basinTierIds = filters.basinTierIds;
    if (Array.isArray(basinTierIds) && basinTierIds.length > 0) {
      payload.basinTierIds = basinTierIds
        .map((v) => Number(v))
        .filter((v) => !Number.isNaN(v));
    }

    // Numeric range filters (acreage / length / width — atom stores them under
    // `min_<key>` and `max_<key>`; payload wants `{key}: { min?, max? }`)
    const numericRanges: Array<'acreage' | 'length' | 'width'> = [
      'acreage',
      'length',
      'width',
    ];
    for (const key of numericRanges) {
      const min = filters[`min_${key}`] as number | undefined;
      const max = filters[`max_${key}`] as number | undefined;
      if (min !== undefined || max !== undefined) {
        payload[key] = {
          ...(min !== undefined && { min }),
          ...(max !== undefined && { max }),
        };
      }
    }

    // Boolean tri-state filters
    for (const key of BOOLEAN_PARAMS) {
      const collapsed = collapseBooleanFilter(filters[key] as string[]);
      if (collapsed !== undefined) {
        (payload as Record<string, unknown>)[key] = collapsed;
      }
    }

    // verification_status is a scalar backend param (not array); extract first element.
    const verificationStatus = filters.verificationStatus;
    if (Array.isArray(verificationStatus) && verificationStatus.length > 0) {
      (payload as Record<string, unknown>).verificationStatus =
        verificationStatus[0];
    }
  }

  return payload;
}
